import type {
  Invoice,
  InvoiceLineItem,
  InvoicePayment,
  InvoiceStatus,
  InvoiceTerms,
  InvoiceWithTotals,
  Payment,
} from "@/features/invoices/types";

function parseTime(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`);
}
function toISO(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}
function addDays(iso: string, days: number): string {
  return toISO(parseTime(iso) + days * 86_400_000);
}
function diffDays(startIso: string, endIso: string): number {
  return Math.round((parseTime(endIso) - parseTime(startIso)) / 86_400_000);
}

export function calcExtendedAmt(quantity: number, unitPriceCents: number): number {
  return Math.round(quantity * unitPriceCents);
}

export function calcSubtotal(items: InvoiceLineItem[]): number {
  return items.reduce((sum, item) => sum + item.extendedAmtCents, 0);
}

export function calcTaxAmt(subtotalCents: number, taxPercent: number): number {
  return Math.round(subtotalCents * (taxPercent / 100));
}

export function calcTotal(subtotalCents: number, taxAmtCents: number): number {
  return subtotalCents + taxAmtCents;
}

/** RECEIPT = same day, NET7/NET21/NET30 = invoiceDate + N days — mirrors the legacy due_date DB trigger. */
export function calcDueDate(invoiceDate: string, terms: InvoiceTerms): string {
  switch (terms) {
    case "RECEIPT":
      return invoiceDate;
    case "NET7":
      return addDays(invoiceDate, 7);
    case "NET21":
      return addDays(invoiceDate, 21);
    case "NET30":
      return addDays(invoiceDate, 30);
  }
}

export function calcApplied(invoicePayments: InvoicePayment[], invoiceId: string): number {
  return invoicePayments
    .filter((ip) => ip.invoiceId === invoiceId)
    .reduce((sum, ip) => sum + ip.amountCents, 0);
}

export function calcBalance(totalCents: number, appliedCents: number): number {
  return totalCents - appliedCents;
}

/** Raw day-diff between due date and asOf — positive means overdue. Callers decide whether/when to display it (OPEN + > 0). */
export function calcAgingDays(dueDate: string, asOf: string = new Date().toISOString().slice(0, 10)): number {
  return diffDays(dueDate, asOf);
}

export function calcUnappliedCents(payment: Payment, invoicePayments: InvoicePayment[]): number {
  const applied = invoicePayments
    .filter((ip) => ip.paymentId === payment.id)
    .reduce((sum, ip) => sum + ip.amountCents, 0);
  return payment.paymentAmtCents - applied;
}

/**
 * DRAFT and VOID never auto-transition. OPEN/PAID are derived purely from
 * balance so the two stay in lockstep regardless of which direction the
 * balance moved (payment added, edited, or removed).
 */
export function deriveInvoiceStatus(currentStatus: InvoiceStatus, balanceCents: number): InvoiceStatus {
  if (currentStatus === "DRAFT" || currentStatus === "VOID") return currentStatus;
  return balanceCents <= 0 ? "PAID" : "OPEN";
}

export function withTotals(
  invoice: Invoice,
  items: InvoiceLineItem[],
  invoicePayments: InvoicePayment[],
  asOf?: string,
): InvoiceWithTotals {
  const subtotalCents = calcSubtotal(items);
  const taxAmtCents = calcTaxAmt(subtotalCents, invoice.taxPercent);
  const totalCents = calcTotal(subtotalCents, taxAmtCents);
  const appliedCents = calcApplied(invoicePayments, invoice.id);
  const balanceCents = calcBalance(totalCents, appliedCents);
  const agingDays = calcAgingDays(invoice.dueDate, asOf);
  const status = deriveInvoiceStatus(invoice.status, balanceCents);

  return {
    ...invoice,
    status,
    taxAmtCents,
    subtotalCents,
    totalCents,
    appliedCents,
    balanceCents,
    agingDays,
  };
}
